import { forwardRef, memo } from 'react';
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import type { CardState, CardFormData, LanguageCode } from '../../types/entryRules.types';
import { UI_LABELS } from '../../constants/languages.constants';
import { getCountryName, getCountriesForLang } from '../../constants/countries.constants';
import { isCardComplete } from '../../utils/entryRules.utils';

interface EntryRuleCardProps {
  card: CardState;
  index: number;
  lang: LanguageCode;
  /** Country codes already taken by another rule; not selectable here. */
  usedCountryCodes: Set<string>;
  onUpdate: (localId: string, field: keyof CardFormData, value: string) => void;
  onSave: (localId: string) => void;
  onDeleteRequest: (localId: string) => void;
  onToggle: (localId: string) => void;
}

// The country list has 200+ entries. Rendering it as its own memoized component,
// keyed only by `lang` and the taken-country set, keeps React from rebuilding those
// elements on every keystroke in the rule-text field (which re-renders the parent card).
const CountrySelectItems = memo(function CountrySelectItems({
  lang,
  usedCountryCodes,
  currentCode,
}: {
  lang: LanguageCode;
  usedCountryCodes: Set<string>;
  currentCode: string;
}) {
  const countries = getCountriesForLang(lang);
  return (
    <>
      {countries.map((c) => (
        <SelectItem
          key={c.value}
          value={c.value}
          // A country already covered by another rule stays visible but unselectable:
          // a rule's country is its Firestore document id, so two rules can't share one.
          disabled={c.value !== currentCode && usedCountryCodes.has(c.value)}
        >
          {c.label}
        </SelectItem>
      ))}
    </>
  );
});

const EntryRuleCardComponent = forwardRef<HTMLDivElement, EntryRuleCardProps>(
  ({ card, index, lang, usedCountryCodes, onUpdate, onSave, onDeleteRequest, onToggle }, ref) => {
    const labels = UI_LABELS[lang];
    const countryDisplayName = card.currentData.countryCode
      ? getCountryName(card.currentData.countryCode, lang)
      : labels.newCountry;

    const canSave =
      card.status !== 'saved' &&
      isCardComplete(card.currentData) &&
      !card.isSaving;

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onUpdate(card.localId, 'ruleText', e.target.value);
    };

    return (
      <div ref={ref} className="border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 bg-white">
          <span className="font-medium text-gray-900">
            {index}. {countryDisplayName}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-red-600 gap-1.5"
              onClick={() => onDeleteRequest(card.localId)}
            >
              <Trash2 className="h-4 w-4" />
              {labels.delete}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-500"
              onClick={() => onToggle(card.localId)}
              aria-label={card.isExpanded ? 'Свернуть' : 'Развернуть'}
            >
              {card.isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {card.isExpanded && (
          <div className="px-4 pb-4 pt-2 border-t border-gray-100 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-normal text-gray-700">
                {labels.country} *
              </Label>
              <Select
                value={card.currentData.countryCode}
                onValueChange={(val) => onUpdate(card.localId, 'countryCode', val)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={`— ${labels.country} —`} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <CountrySelectItems
                    lang={lang}
                    usedCountryCodes={usedCountryCodes}
                    currentCode={card.currentData.countryCode}
                  />
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-normal text-gray-700">
                {labels.ruleText} *
              </Label>
              <Textarea
                value={card.currentData.ruleText}
                onChange={handleTextareaChange}
                placeholder={labels.placeholder}
                rows={6}
                className="resize-none"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <span
                className={`text-sm ${
                  card.status === 'saved' ? 'text-green-600' : 'text-amber-600'
                }`}
              >
                {card.status === 'saved' ? labels.saved : labels.unsaved}
              </span>
              <Button
                onClick={() => onSave(card.localId)}
                disabled={!canSave}
                size="sm"
                className="min-w-[120px]"
              >
                {card.isSaving ? 'Сохранение...' : labels.save}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

EntryRuleCardComponent.displayName = 'EntryRuleCard';

// Memoized so typing in one card's rule-text field does not re-render every
// other card. Relies on the parent passing stable callback references.
export const EntryRuleCard = memo(EntryRuleCardComponent);
